/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { callApi } from 'api/helpers/api.helpers';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import addressBookApi from '../../../api/addressBook.api';
import { selectAuthUserData } from '../../../redux/features/auth/auth.slice';

export default function useDuplicateImportContactDialog(importResult, onAllDuplicatesResolved, onCancelImport) {
  const [strategy, setStrategy] = useState(null);
  const [applyToAll, setApplyToAll] = useState(false);
  const [duplicatesPage, setDuplicatesPage] = useState(null);
  const [currentDuplicateIndex, setCurrentDuplicateIndex] = useState(0);
  const [resolvedList, setResolvedList] = useState([]);
  const [resolvedAll, setResolvedAll] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const userData = useSelector(selectAuthUserData);
  const currentDuplicate = duplicatesPage?.content?.[currentDuplicateIndex];
  const isLastDuplicate = currentDuplicateIndex + 1 === duplicatesPage?.content?.length;
  const hasAnotherPage = !duplicatesPage?.last;

  /* eslint-disable-next-line camelcase */
  const localStorageKey = `ADDRESSBOOK-IMPORT-DUPLICATES-${userData.given_name}`;

  const getDuplicatesPage = async (page, pageSize) => {
    const duplicatesResponse = await callApi(addressBookApi.getImportDuplicates)(importResult.id, page, pageSize);
    setDuplicatesPage(duplicatesResponse);
    setCurrentDuplicateIndex(currentDuplicateIndex);
  };

  useEffect(() => {
    const localStorageDuplicates = localStorage.getItem(localStorageKey);
    if (localStorageDuplicates) {
      const duplicatesStored = JSON.parse(localStorageDuplicates);
      if (duplicatesStored.importId === importResult.id) {
        setDuplicatesPage(duplicatesStored.duplicatesPage);
        setCurrentDuplicateIndex(duplicatesStored.currentDuplicateIndex);
        setResolvedList(duplicatesStored.resolvedList);
      } else {
        localStorage.removeItem(localStorageKey);
        getDuplicatesPage(0, 10);
      }
    } else {
      getDuplicatesPage(0, 10);
    }
  }, [importResult]);

  useEffect(() => {
    if (resolvedAll) {
      onAllDuplicatesResolved(resolvedList);
    }
  }, [resolvedAll]);

  const storeDuplicatesOngoingImport = (newList) => {
    const duplicatesStored = {
      duplicatesPage,
      currentDuplicateIndex: currentDuplicateIndex + 1,
      resolvedList: newList,
      importId: importResult.id,
    };
    localStorage.setItem(localStorageKey, JSON.stringify(duplicatesStored));
  };

  const storeResolvedList = (newResolvedList) => {
    const newList = [...resolvedList, ...newResolvedList];
    setResolvedList(newList);
    storeDuplicatesOngoingImport(newList);
  };

  const loadNextPage = (numberOfItemsToLoad) => {
    if (hasAnotherPage) {
      getDuplicatesPage(duplicatesPage.number + 1, numberOfItemsToLoad);
    } else {
      localStorage.removeItem(localStorageKey);
      setResolvedAll(true);
    }
  };

  const handleStrategy = async (selectedStrategy) => {
    if (applyToAll) {
      do {
        const newResolvedList = duplicatesPage.content.map(duplicate => ({
          partyId: duplicate.party.id,
          strategy: selectedStrategy,
        }));
        storeResolvedList(newResolvedList);
        loadNextPage(duplicatesPage.totalElements);
      } while (hasAnotherPage);
    } else {
      storeResolvedList([{ partyId: currentDuplicate.party.id, strategy: selectedStrategy }]);
      if (isLastDuplicate) {
        loadNextPage(10);
      } else {
        setCurrentDuplicateIndex(currentDuplicateIndex + 1);
      }
    }
  };

  const onStrategyChange = ev => setStrategy(ev.target.value);
  const onApplyToAllChange = () => setApplyToAll(!applyToAll);
  const onImport = () => {
    handleStrategy(strategy);
    setStrategy(null);
  };
  const onCancel = () => {
    setStrategy(null);
    setApplyToAll(false);
    setDuplicatesPage(null);
    setCurrentDuplicateIndex(0);
    setResolvedList([]);
    localStorage.removeItem(localStorageKey);
    setResolvedAll(false);
    onCancelImport();
  };

  return {
    strategy,
    applyToAll,
    duplicatesPage,
    currentDuplicate,
    resolvedList,
    setApplyToAll,
    onStrategyChange,
    onApplyToAllChange,
    onImport,
    onCancel,
    showCancelConfirm,
    setShowCancelConfirm,
  };
}
